/**
 * ================================================================
 * FILE METADATA & OVERVIEW
 * ================================================================
 *
 * @fileOverview     Tab-based routing types for URL parameter management
 * @module           RoutingTypes
 * @version          1.0.0
 * @since            2025-08-18
 * @author           Developer <dev@domain.com>
 *
 * @purpose          Centralized type definitions for tab-based routing system
 * @dependencies     None - Pure TypeScript types
 * @exports          Feature definitions, app mappings, route types
 *
 * @architecture
 * - Design patterns: Const assertions for type safety
 * - Integration points: URL parameter validation
 * - State management: Query parameter synchronization
 *
 * @performance
 * - Performance considerations: Compile-time type checking
 * - Optimization techniques: Const assertions for smaller bundle size
 * - Memory usage: Minimal runtime overhead
 *
 * @accessibility    N/A - Type definitions only
 * @testing          Type-level testing through usage validation
 *
 * ================================================================
 */

// ================================================================
// PROFILE APPLICATION FEATURES
// ================================================================
//
// Profile feature tabs mapping with const assertion for type safety
// These correspond to the existing profile tabs in the UI
// ================================================================

export const profileFeatures = {
  basic: 'basic',
  contact: 'contact',
  registration: 'registration',
  banking: 'banking',
  broker: 'broker',
  ctcl: 'ctcl',
  compliance: 'compliance',
} as const;

export type ProfileFeature = keyof typeof profileFeatures;

// ================================================================
// ACCOUNTS APPLICATION FEATURES
// ================================================================
//
// Account feature tabs mapping for financial management
// Corresponds to existing account routing structure
// ================================================================

export const accountsFeatures = {
  'global-summary': 'global-summary',
  'client-dr-cr': 'client-dr-cr',
  aging: 'aging',
  'late-payment-charges': 'late-payment-charges',
  'risk-common': 'risk-common',
} as const;

export type AccountsFeature = keyof typeof accountsFeatures;

// ================================================================
// REPORTS APPLICATION FEATURES
// ================================================================
//
// Report feature tabs mapping for document management
// Supports existing report categories and types
// ================================================================

export const reportsFeatures = {
  ledger: 'ledger',
  holding: 'holding',
  position: 'position',
  global: 'global',
  'income-tax': 'income-tax',
  'contract-bill': 'contract-bill',
} as const;

export type ReportsFeature = keyof typeof reportsFeatures;

// ================================================================
// IPO APPLICATION FEATURES
// ================================================================
//
// IPO feature tabs mapping for investment management
// Handles online and physical IPO processes
// ================================================================

export const ipoFeatures = {
  'all-ipos': 'all-ipos',
  online: 'online',
  physical: 'physical',
  applications: 'applications',
  watchlist: 'watchlist',
  performance: 'performance',
} as const;

export type IPOFeature = keyof typeof ipoFeatures;

// ================================================================
// WEALTH APPLICATION FEATURES
// ================================================================
//
// Wealth management feature tabs mapping
// Covers mutual funds, insurance, and PMS services
// ================================================================

export const wealthFeatures = {
  dashboard: 'dashboard',
  'mutual-fund': 'mutual-fund',
  insurance: 'insurance',
  pms: 'pms',
  analytics: 'analytics',
  portfolio: 'portfolio',
} as const;

export type WealthFeature = keyof typeof wealthFeatures;

// ================================================================
// MTF APPLICATION FEATURES
// ================================================================
//
// MTF feature tabs mapping for margin funding management
// Supports overview, KYC, positions, risk monitoring, analytics
// ================================================================

export const mtfFeatures = {
  dashboard: 'dashboard',
  overview: 'overview',
  kyc: 'kyc',
  positions: 'positions',
  risk: 'risk',
  analytics: 'analytics',
} as const;

export type MTFFeature = keyof typeof mtfFeatures;

// ================================================================
// KYC APPLICATION FEATURES
// ================================================================
//
// KYC feature tabs mapping for compliance management
// Handles document verification and status tracking
// ================================================================

export const kycFeatures = {
  dashboard: 'dashboard',
  profile: 'profile',
  modification: 'modification',
  applications: 'applications',
  compliance: 'compliance',
  monitoring: 'monitoring',
  onboarding: 'onboarding',
} as const;

export type KYCFeature = keyof typeof kycFeatures;

// ================================================================
// COMPLIANCE APPLICATION FEATURES
// ================================================================
//
// Compliance feature tabs mapping for regulatory management
// Handles compliance checks and reporting
// ================================================================

export const complianceFeatures = {
  'peak-margin': 'peak-margin',
  'kyc-incomplete': 'kyc-incomplete',
  't5-monitoring': 't5-monitoring',
  'nptt-alerts': 'nptt-alerts',
  'short-margin': 'short-margin',
  history: 'history',
} as const;

export type ComplianceFeature = keyof typeof complianceFeatures;

// ================================================================
// CENTRALIZED APP FEATURES MAPPING
// ================================================================
//
// Master mapping of all applications to their feature sets
// Enables type-safe feature validation across the system
// ================================================================

export const appFeatures = {
  profile: profileFeatures,
  accounts: accountsFeatures,
  reports: reportsFeatures,
  ipo: ipoFeatures,
  wealth: wealthFeatures,
  mtf: mtfFeatures,
  kyc: kycFeatures,
  compliance: complianceFeatures,
} as const;

// ================================================================
// APPLICATION TYPE DEFINITIONS
// ================================================================
//
// Core type definitions for type-safe routing
// Ensures compile-time validation of app names and features
// ================================================================

export type AppName = keyof typeof appFeatures;

export type AppFeature<T extends AppName> = keyof (typeof appFeatures)[T];

// ================================================================
// ROUTE CONFIGURATION TYPES
// ================================================================
//
// Types for route generation and validation
// Supports flexible routing patterns with type safety
// ================================================================

export interface RouteConfig {
  app: AppName;
  basePath: string;
  defaultFeature: string;
  requiresAuth: boolean;
}

export interface TabRoute {
  app: AppName;
  feature: string;
  url: string;
}

export interface NavigationItem {
  label: string;
  url: string;
  isActive?: boolean;
}

// ================================================================
// URL PARAMETER TYPES
// ================================================================
//
// Types for URL parameter management and validation
// Handles query string parsing and sanitization
// ================================================================

export interface URLParams {
  feature?: string;
  [key: string]: string | undefined;
}

export interface RouteValidationResult {
  isValid: boolean;
  sanitizedFeature: string;
  errors?: string[];
}

/**
 * ================================================================
 * END OF FILE: RoutingTypes
 * ================================================================
 *
 * @summary          Centralized type definitions for tab-based routing
 * @exports          Feature mappings, app types, route configuration types
 * @dependencies     None - pure TypeScript types
 * @usedBy           Route utilities, navigation components, page components
 *
 * @maintainers
 * - Primary: Developer <dev@domain.com>
 *
 * @changelog
 * - 2025-08-18: Initial implementation with all app feature mappings
 *
 * @todos
 * - [ ] Add support for nested feature categories
 * - [ ] Implement role-based feature access control
 * - [ ] Add breadcrumb generation types
 *
 * ================================================================
 */
